/**
 * IdeaCascadeModal - Modal for reviewing and approving cascade changes
 *
 * Shows:
 * - The direct change being made
 * - Idea-level cascades (features, assumptions)
 * - Plan-level cascades (features, stories, event model) grouped by plan
 * - Approve/reject controls for each cascade
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Text,
  Caption,
  Badge,
  tokens,
} from '../design-system';
import TextDiff from './TextDiff';

// Severity badge colors
const SEVERITY_STYLES = {
  required: {
    bg: 'rgba(239, 68, 68, 0.1)',
    color: 'rgb(239, 68, 68)',
    label: 'Required — cannot be skipped',
  },
  recommended: {
    bg: 'rgba(234, 179, 8, 0.1)',
    color: 'rgb(234, 179, 8)',
    label: 'Recommended',
  },
  optional: {
    bg: 'rgba(100, 116, 139, 0.1)',
    color: 'rgb(100, 116, 139)',
    label: 'Optional',
  },
};

// Cascade type icons and labels
const CASCADE_TYPES = {
  feature: { icon: '✨', label: 'Feature' },
  assumption: { icon: '💡', label: 'Assumption' },
  feature_group: { icon: '📚', label: 'Feature' },
  story: { icon: '📝', label: 'Story' },
  event_model: { icon: '⚡', label: 'Event Model' },
  roadmap: { icon: '🗺️', label: 'Roadmap' },
};

const IdeaCascadeModal = ({
  isOpen,
  onClose,
  directChange,
  cascadeChanges = [],
  affectedPlans = [],
  summary,
  warnings = [],
  onApply,
  isApplying = false,
}) => {
  // Track which cascades are approved (default: all recommended/required)
  const [approvedIds, setApprovedIds] = useState(new Set());

  // Initialize approved cascades only when modal opens (not on every re-render)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (justOpened && cascadeChanges.length > 0) {
      const initialApproved = new Set();
      cascadeChanges.forEach((cascade, idx) => {
        if (cascade.severity === 'required' || cascade.severity === 'recommended') {
          initialApproved.add(idx);
        }
      });
      setApprovedIds(initialApproved);
    }
  }, [isOpen, cascadeChanges]);

  // Separate idea-level and plan-level cascades
  const ideaCascades = cascadeChanges
    .map((c, idx) => ({ ...c, _idx: idx }))
    .filter(c => !c.plan_id);

  const planCascades = cascadeChanges
    .map((c, idx) => ({ ...c, _idx: idx }))
    .filter(c => c.plan_id);

  // Group plan cascades by plan
  const cascadesByPlan = planCascades.reduce((acc, cascade) => {
    const planId = cascade.plan_id;
    if (!acc[planId]) {
      acc[planId] = {
        planName: cascade.plan_name || 'Unknown Plan',
        cascades: [],
      };
    }
    acc[planId].cascades.push(cascade);
    return acc;
  }, {});

  // Group idea cascades by type
  const ideaCascadesByType = ideaCascades.reduce((acc, cascade) => {
    const type = cascade.cascade_type || 'feature';
    if (!acc[type]) acc[type] = [];
    acc[type].push(cascade);
    return acc;
  }, {});

  const toggleCascade = (idx) => {
    const cascade = cascadeChanges[idx];
    // Required cascades cannot be deselected
    if (cascade.severity === 'required') return;

    setApprovedIds(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const handleApply = () => {
    const approved = cascadeChanges.filter((_, idx) => approvedIds.has(idx));
    onApply(approved);
  };

  const handleSkip = () => {
    onApply([]);  // Apply with no cascades
  };

  if (!isOpen) return null;

  const hasChanges = cascadeChanges.length > 0;
  const ideaCascadeCount = ideaCascades.length;
  const planCascadeCount = planCascades.length;

  const renderCascadeItem = (cascade) => {
    const severity = SEVERITY_STYLES[cascade.severity] || SEVERITY_STYLES.recommended;
    const isApproved = approvedIds.has(cascade._idx);
    const isRequired = cascade.severity === 'required';
    const typeConfig = CASCADE_TYPES[cascade.cascade_type] || CASCADE_TYPES.feature;

    return (
      <div
        key={cascade._idx}
        onClick={() => toggleCascade(cascade._idx)}
        style={{
          padding: tokens.spacing[3],
          backgroundColor: isApproved ? `${tokens.colors.primary[500]}15` : 'var(--color-surface)',
          border: `1px solid ${isApproved ? tokens.colors.primary[500] : 'var(--color-border)'}`,
          borderRadius: tokens.borderRadius.md,
          cursor: isRequired ? 'not-allowed' : 'pointer',
          opacity: isRequired ? 0.9 : 1,
          transition: `all ${tokens.transitionDuration.fast}`,
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: tokens.spacing[2],
        }}>
          <input
            type="checkbox"
            checked={isApproved}
            disabled={isRequired}
            onChange={() => {}}
            title={isRequired ? 'This change is required and cannot be skipped' : undefined}
            style={{ marginTop: '4px', cursor: isRequired ? 'not-allowed' : 'pointer' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing[2],
              marginBottom: tokens.spacing[1],
              flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: '14px' }}>{typeConfig.icon}</span>
              <Text style={{
                fontWeight: tokens.typography.fontWeight.medium,
                margin: 0,
              }}>
                {cascade.target_name}
              </Text>
              <span style={{
                fontSize: '0.75rem',
                padding: `2px ${tokens.spacing[2]}`,
                borderRadius: tokens.borderRadius.sm,
                backgroundColor: severity.bg,
                color: severity.color,
              }}>
                {severity.label}
              </span>
            </div>
            <Caption style={{ display: 'block', marginBottom: tokens.spacing[1] }}>
              <strong>{cascade.field}:</strong>
            </Caption>
            {(cascade.old_value != null || cascade.new_value != null) && (
              <div style={{ marginBottom: tokens.spacing[1] }}>
                <TextDiff oldText={cascade.old_value} newText={cascade.new_value} />
              </div>
            )}
            <Caption style={{ color: 'var(--color-text-muted)' }}>
              {cascade.reason}
            </Caption>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Review Changes"
      size="lg"
    >
      <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        {/* Direct Change */}
        <div style={{
          padding: tokens.spacing[4],
          backgroundColor: 'var(--color-surface)',
          borderRadius: tokens.borderRadius.md,
          border: '1px solid var(--color-border)',
          marginBottom: tokens.spacing[4],
        }}>
          <Text style={{
            fontWeight: tokens.typography.fontWeight.semibold,
            marginBottom: tokens.spacing[2],
          }}>
            Direct Change
          </Text>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: tokens.spacing[3],
          }}>
            <Badge variant="primary">{directChange?.section}</Badge>
            <div style={{ flex: 1 }}>
              <TextDiff oldText={directChange?.old_value} newText={directChange?.new_value} />
            </div>
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div style={{
            padding: tokens.spacing[3],
            backgroundColor: `${tokens.colors.primary[500]}10`,
            borderRadius: tokens.borderRadius.md,
            marginBottom: tokens.spacing[4],
          }}>
            <Text style={{ margin: 0 }}>{summary}</Text>
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div style={{
            padding: tokens.spacing[3],
            backgroundColor: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: tokens.borderRadius.md,
            marginBottom: tokens.spacing[4],
          }}>
            {warnings.map((warning, idx) => (
              <Text key={idx} style={{ margin: 0, color: 'rgb(239, 68, 68)', fontSize: '0.875rem' }}>
                {warning}
              </Text>
            ))}
          </div>
        )}

        {/* Idea-Level Cascades */}
        {ideaCascadeCount > 0 && (
          <div style={{ marginBottom: tokens.spacing[6] }}>
            <Text style={{
              fontWeight: tokens.typography.fontWeight.semibold,
              marginBottom: tokens.spacing[3],
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing[2],
            }}>
              <span>💡</span>
              Idea Updates ({ideaCascadeCount})
            </Text>

            {Object.entries(ideaCascadesByType).map(([type, changes]) => (
              <div key={type} style={{ marginBottom: tokens.spacing[4] }}>
                <Caption style={{
                  display: 'block',
                  marginBottom: tokens.spacing[2],
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {CASCADE_TYPES[type]?.label || type} ({changes.length})
                </Caption>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.spacing[2],
                }}>
                  {changes.map(renderCascadeItem)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Plan-Level Cascades */}
        {planCascadeCount > 0 && (
          <div style={{ marginBottom: tokens.spacing[4] }}>
            <Text style={{
              fontWeight: tokens.typography.fontWeight.semibold,
              marginBottom: tokens.spacing[3],
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing[2],
            }}>
              <span>📁</span>
              Plan Updates ({planCascadeCount})
            </Text>

            {Object.entries(cascadesByPlan).map(([planId, { planName, cascades }]) => (
              <div key={planId} style={{
                marginBottom: tokens.spacing[4],
                padding: tokens.spacing[3],
                backgroundColor: 'var(--color-background)',
                borderRadius: tokens.borderRadius.md,
                border: '1px solid var(--color-border)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacing[2],
                  marginBottom: tokens.spacing[3],
                }}>
                  <Badge variant="secondary">{planName}</Badge>
                  <Caption>{cascades.length} change{cascades.length !== 1 ? 's' : ''}</Caption>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.spacing[2],
                }}>
                  {cascades.map(renderCascadeItem)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No changes message */}
        {!hasChanges && (
          <div style={{
            padding: tokens.spacing[4],
            textAlign: 'center',
            color: 'var(--color-text-muted)',
          }}>
            <Text>No cascade effects detected. The change is isolated.</Text>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: tokens.spacing[3],
        paddingTop: tokens.spacing[4],
        borderTop: '1px solid var(--color-border)',
        marginTop: tokens.spacing[4],
      }}>
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={isApplying}
        >
          Cancel
        </Button>
        {cascadeChanges.length > 0 && (
          <Button
            variant="secondary"
            onClick={handleSkip}
            disabled={isApplying}
          >
            Apply Without Cascades
          </Button>
        )}
        <Button
          variant="primary"
          onClick={handleApply}
          loading={isApplying}
          disabled={isApplying}
        >
          {cascadeChanges.length > 0
            ? `Apply ${approvedIds.size} Change${approvedIds.size !== 1 ? 's' : ''}`
            : 'Apply Change'}
        </Button>
      </div>
    </Modal>
  );
};

export default IdeaCascadeModal;
